/**
 * Camera Scanner Configuration
 * 
 * Centralized configuration for camera scanner component.
 * All magic numbers and thresholds are defined here for easy tuning.
 */

export const CAMERA_CONFIG = {
  /**
   * Image Capture Settings
   */
  CAPTURE: {
    /** Upscale factor for captured images (3x = triple resolution for ultra-sharp QR codes) */
    UPSCALE_FACTOR: 3.0,
    
    /** JPEG quality for high-quality captures (0.0 - 1.0) */
    JPEG_QUALITY_HIGH: 0.98,
    
    /** JPEG quality for medium-quality captures (0.0 - 1.0) */
    JPEG_QUALITY_MEDIUM: 0.92,
    
    /** JPEG quality for Pica-optimized images (0.0 - 1.0) */
    JPEG_QUALITY_PICA: 0.98,
    
    /** Padding around QR code in pixels (scaled to video coordinates) */
    QR_PADDING: 20,
    
    /** Minimum sharpness score (Laplacian variance) to accept an image (higher is sharper) */
    MIN_SHARPNESS: 50,
    
    /** Minimum sharpness for small QR codes (higher threshold for better quality) */
    MIN_SHARPNESS_SMALL_QR: 65, // Increased threshold for small QR codes
    
    /** Maximum number of retries if image is blurry */
    MAX_BLUR_RETRIES: 3,
    
    /** Delay between blur retries in ms (allows autofocus to settle) */
    BLUR_RETRY_DELAY_MS: 300,
    
    /** Burst capture settings for small QR codes */
    BURST: {
      /** Number of frames to capture in burst mode */
      FRAME_COUNT: 9, // Increased from 7 to 9 for better chance of sharp frame
      
      /** Delay between burst frames (milliseconds) */
      FRAME_DELAY_MS: 150, // Increased from 120ms to allow more focus settling time
      
      /** Minimum acceptable sharpness for burst frames */
      MIN_SHARPNESS: 60, // Increased threshold for burst selection
      
      /** Early termination threshold - stop if we get a frame this sharp */
      EARLY_TERMINATION_SHARPNESS: 100,
      
      /** Minimum frames to capture before early termination can occur */
      MIN_FRAMES_BEFORE_EARLY_TERMINATION: 3,
      
      /** Fast blur detection threshold - skip full processing if below this */
      FAST_BLUR_THRESHOLD: 30,
      
      /** Adaptive delay - increase if sharpness improving, decrease if stable */
      ADAPTIVE_DELAY_ENABLED: true,
      MIN_DELAY_MS: 100,
      MAX_DELAY_MS: 250,
      
      /** Use actual QR code coordinates for sharpness calculation */
      USE_QR_COORDINATES: true,
      
      /** Progressive quality tracking - track sharpness trends */
      TRACK_PROGRESSIVE_QUALITY: true,
    },
  },

  /**
   * Exposure Compensation Settings
   */
  EXPOSURE: {
    /** Brightness threshold for very dark scenes (0-255) */
    BRIGHTNESS_THRESHOLD_DARK: 60,
    
    /** Brightness threshold for moderately dark scenes (0-255) */
    BRIGHTNESS_THRESHOLD_MODERATE: 100,
    
    /** Brightness threshold for bright scenes (0-255) */
    BRIGHTNESS_THRESHOLD_BRIGHT: 180,
    
    /** Exposure compensation for very dark backgrounds */
    COMPENSATION_DARK: -2.0,
    
    /** Exposure compensation for moderately dark scenes */
    COMPENSATION_MODERATE: -1.0,
    
    /** Exposure compensation for bright scenes */
    COMPENSATION_BRIGHT: -2.0,
    
    /** Exposure compensation for normal lighting */
    COMPENSATION_NEUTRAL: 0,
    
    /** Interval for checking scene brightness (milliseconds) */
    MONITORING_INTERVAL_MS: 2000,
    
    /** Sample canvas size for brightness analysis */
    SAMPLE_WIDTH: 160,
    SAMPLE_HEIGHT: 120,
  },

  /**
   * Camera Zoom Settings
   */
  ZOOM: {
    /** Optimal zoom level for QR code scanning */
    OPTIMAL_LEVEL: 2.5,
    
    /** Optimal zoom level for small QR codes (higher zoom for better visibility) */
    OPTIMAL_LEVEL_SMALL_QR: 3.0, // Increased zoom for small QR codes
    
    /** Maximum zoom level */
    MAX_LEVEL: 4.0,
    
    /** Minimum zoom level */
    MIN_LEVEL: 1.0,
  },

  /**
   * QR Code Detection Settings
   */
  DETECTION: {
    /** Throttle time between image captures (milliseconds) */
    THROTTLE_MS: 50,
    
    /** Debounce time for QR code detection (milliseconds) */
    DEBOUNCE_MS: 300,
    
    /** Maximum number of upload retries */
    MAX_RETRIES: 3,
    
    /** Minimum QR code size to be considered valid (pixels) */
    MIN_QR_SIZE: 20,
    
    /** Maximum QR code size to be considered valid (pixels) */
    MAX_QR_SIZE: 1000,
  },

  /**
   * Camera Resolution Constraints
   */
  RESOLUTION: {
    /** 4K Ultra HD resolution */
    UHD_4K: {
      width: { ideal: 3840, min: 1920 },
      height: { ideal: 2160, min: 1080 },
    },
    
    /** Full HD resolution */
    FULL_HD: {
      width: { ideal: 1920, min: 1280 },
      height: { ideal: 1080, min: 720 },
    },
    
    /** HD resolution */
    HD: {
      width: { ideal: 1280, min: 640 },
      height: { ideal: 720, min: 480 },
    },
    
    /** Standard definition */
    SD: {
      width: { ideal: 640, min: 480 },
      height: { ideal: 480, min: 360 },
    },
  },

  /**
   * Focus Settings
   */
  FOCUS: {
    /** Ideal focus distance for QR codes (meters) - optimized for small QR codes */
    DISTANCE_IDEAL: 0.18, // Reduced from 0.25m to 0.18m (18cm) for better small QR code focus
    
    /** Ideal focus distance for small QR codes specifically (meters) */
    DISTANCE_IDEAL_SMALL_QR: 0.15, // 15cm for very small QR codes
    
    /** Minimum focus distance (meters) */
    DISTANCE_MIN: 0.1,
    
    /** Maximum focus distance for small QR codes (meters) - tighter range for better focus */
    DISTANCE_MAX: 0.35, // Reduced from 0.6m to 0.35m to keep focus closer
    
    /** Maximum focus distance for normal QR codes (meters) */
    DISTANCE_MAX_NORMAL: 0.6,
    
    /** Delay after focus lock before capture (milliseconds) - increased for small QR codes */
    LOCK_DELAY_MS: 800, // Increased from 600ms to allow more time for focus to settle
    
    /** Delay for single-shot focus mode (milliseconds) */
    SINGLE_SHOT_DELAY_MS: 1000, // Increased delay for single-shot to ensure focus is locked
  },

  /**
   * Image Processing Settings
   */
  PROCESSING: {
    /** Target size for Pica optimization (pixels) */
    PICA_TARGET_SIZE: 1536,
    
    /** Pica quality setting (0-3, 3 = highest) */
    PICA_QUALITY: 3,
    
    /** Pica unsharp masking amount */
    PICA_UNSHARP_AMOUNT: 80,
    
    /** Pica unsharp radius */
    PICA_UNSHARP_RADIUS: 0.6,
    
    /** Pica unsharp threshold */
    PICA_UNSHARP_THRESHOLD: 2,
  },

  /**
   * Enhancement Settings
   */
  ENHANCEMENT: {
    /** Mobile-optimized enhancement */
    MOBILE: {
      brightness: 1.1,
      contrast: 1.2,
    },
    
    /** Small QR code enhancement */
    SMALL_QR: {
      brightness: 1.6,
      contrast: 2.5,
      saturate: 1.4,
    },
    
    /** Ultra-aggressive enhancement for tiny QR codes */
    ULTRA: {
      brightness: 2.0,
      contrast: 3.0,
      saturate: 1.5,
    },
  },

  /**
   * Timeout Settings
   */
  TIMEOUTS: {
    /** Video loading timeout (milliseconds) */
    VIDEO_LOAD_MS: 5000,
    
    /** Camera initialization delay (milliseconds) */
    CAMERA_INIT_DELAY_MS: 50,
    
    /** Torch/zoom check delay (milliseconds) */
    FEATURE_CHECK_DELAY_MS: 500,
    
    /** Idle timeout before closing camera (milliseconds) - 3 minutes */
    IDLE_TIMEOUT_MS: 180000,
    
    /** Warning time before idle timeout (milliseconds) - 30 seconds */
    IDLE_WARNING_MS: 30000,

    /** QR detection timeout (milliseconds) - 30 seconds */
    QR_DETECTION_TIMEOUT_MS: 30000,
  },

  /**
   * Frame Rate Settings
   */
  FRAME_RATE: {
    /** Ideal frame rate */
    IDEAL: 30,
    
    /** Minimum acceptable frame rate */
    MIN: 15,
  },
} as const;

/**
 * Type-safe access to configuration values
 */
export type CameraConfig = typeof CAMERA_CONFIG;
